import React, { Component, ErrorInfo, FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'react-bootstrap'

type ErrorBoundaryProps = {
    children: ReactNode
    /**
     * Changing this clears the error. Pass the current route so that one page crashing does not
     * wedge the whole session behind a manual reload.
     */
    resetKey?: string
}

type ErrorBoundaryState = {
    error: Error | null
    /** Mirrors props.resetKey so getDerivedStateFromProps can tell a route change from a throw. */
    resetKey?: string
}

/** Rendered in place of the crashed subtree. A function component so it can use hooks. */
const ErrorFallback: FC<{ error: Error }> = ({ error }) => {
    const { t } = useTranslation()

    return (
        <div className="d-flex flex-column justify-content-center align-items-center text-center p-5 w-100">
            <i className="fa-solid fa-triangle-exclamation fa-2x text-warning mb-3"></i>
            <h5>{t('app.error_boundary_title')}</h5>
            <p className="text-muted">{t('app.error_boundary_body')}</p>
            <Button variant="primary" onClick={() => window.location.reload()}>{t('app.reload')}</Button>

            {'development' === process.env.NODE_ENV &&
                <pre className="mt-4 text-start small text-muted overflow-x-auto">{error.message}</pre>
            }
        </div>
    )
}

/**
 * Catches render/commit errors so one broken subtree does not take the whole SPA down.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    public state: ErrorBoundaryState = { error: null }

    public static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        return { error }
    }

    /**
     * Clears the error when the route changes.
     *
     * Tracking resetKey in state is what makes this safe: when a child throws, props have not
     * changed, so the keys match and this returns null rather than wiping the error React just set.
     */
    public static getDerivedStateFromProps(
        props: ErrorBoundaryProps,
        state: ErrorBoundaryState,
    ): Partial<ErrorBoundaryState> | null {
        return props.resetKey === state.resetKey ? null : { error: null, resetKey: props.resetKey }
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // eslint-disable-next-line no-console
        console.error('[ErrorBoundary]', error, errorInfo.componentStack)
    }

    public render(): ReactNode {
        return this.state.error ? <ErrorFallback error={this.state.error} /> : this.props.children
    }
}

export default ErrorBoundary
